import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import { ScanMedia } from 'features/scan-media'

import { IScanDocument, ScanDocument } from 'shared/api/scan-document'
import PassExampleId from 'shared/images/frontId.webp'
import PassExamplePassport from 'shared/images/frontPassword.webp'
import { getErrorMessage } from 'shared/lib/errorText'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { Button } from 'shared/ui/button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { ImageLazy } from 'shared/ui/image-lazy'
import { Loader } from 'shared/ui/loader'

import { useInsuranceDraft } from '../../model/insurance-draft'
import { createEmptyInsurancePolicyholder } from '../../model/policyholder'

import { mergeScannedPersonData } from './lib/merge-scanned-person'
import s from './styles.module.scss'

const ScanPassportInsurancePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { closeDialog, openDialog } = useDialogBottomSheet()
  const { draft, setPolicyholderData } = useInsuranceDraft()
  const [isLoading, setIsLoading] = useState(false)
  const [showCamera, setShowCamera] = useState(false)

  const person = searchParams.get('person')
  const participantIndex = Number(searchParams.get('participant'))
  const isParticipant =
    person === 'participant' && Number.isInteger(participantIndex)

  const onContinue = (data: IScanDocument) => {
    if (isParticipant) {
      const participants = [...draft.participants]

      if (!participants[participantIndex]) return

      participants[participantIndex] = mergeScannedPersonData(
        participants[participantIndex],
        data,
      )

      setPolicyholderData(
        draft.policyholder || createEmptyInsurancePolicyholder(),
        participants,
      )
    } else {
      const policyholder =
        draft.policyholder || createEmptyInsurancePolicyholder()

      setPolicyholderData(
        mergeScannedPersonData(policyholder, data),
        draft.participants,
      )
    }

    closeDialog()
    const state = isParticipant
      ? { person: 'participant', participantIndex }
      : { person: 'policyholder' }

    navigate(RoutingPaths.insurance.policyholder.path(), {
      replace: true,
      state,
    })
  }

  const onShowError = (text: string) => {
    openDialog({
      isDialog: false,
      child: (
        <div className={s.dialog}>
          <p className={clsx(s.dialogTitle, 'bold-20')}>
            {t('scan_passport.scan_error')}
          </p>
          <p className={clsx(s.dialogText, 'regular-14')}>{text}</p>
          <Button onClick={closeDialog}>{t('close')}</Button>
        </div>
      ),
    })
  }

  const onMakePhoto = async (photo?: File) => {
    if (!photo) {
      onShowError(t('scan_passport.error_scaning_password'))

      return
    }

    setIsLoading(true)
    setShowCamera(false)

    try {
      const formData = new FormData()

      formData.append('file', photo)

      const { data: scanResult } = await ScanDocument.scanUserDocument(formData)

      openDialog({
        isDialog: false,
        child: (
          <div className={s.dialog}>
            <p className={clsx(s.dialogTitle, 'bold-20')}>
              {t('scan_passport.check_your_data')}
            </p>
            <div className={s.dataList}>
              <p className={clsx(s.dataItem, 'regular-14')}>
                {t('last_name')}: {scanResult.data.last_name || '-'}
              </p>
              <p className={clsx(s.dataItem, 'regular-14')}>
                {t('first_name')}: {scanResult.data.first_name || '-'}
              </p>
              <p className={clsx(s.dataItem, 'regular-14')}>
                {t('birth_day')}: {scanResult.data.date_of_birth || '-'}
              </p>
              <p className={clsx(s.dataItem, 'regular-14')}>
                {t('serial_documents')}: {scanResult.data.id_card_number || '-'}
              </p>
            </div>
            <div className={s.dialogButtons}>
              <Button onClick={() => onContinue(scanResult.data)}>Ок</Button>
              <Button
                variant="transparentBlue"
                onClick={() => {
                  closeDialog()
                  setShowCamera(true)
                }}
              >
                {t('one_more')}
              </Button>
            </div>
          </div>
        ),
      })
    } catch (error) {
      onShowError(
        getErrorMessage(error) || t('scan_passport.error_scaning_password'),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onCameraError = () => {
    setShowCamera(false)
    onShowError(t('scan_passport.error_open'))
  }

  return (
    <>
      {showCamera ? (
        <ScanMedia
          onMakeShot={onMakePhoto}
          onError={onCameraError}
          mask={<div className={s.crop} />}
        />
      ) : null}

      <div className={clsx('container', s.page)}>
        <div className={s.header}>
          <HeaderGoBack
            title={t('scan_passport.how_to_scan')}
            buttonWhite
          />
        </div>

        {isLoading ? (
          <Loader marginTop={150} />
        ) : (
          <div className={s.content}>
            <div className={s.wrapper}>
              <h2 className={clsx(s.scanTitle, 'regular-13')}>
                {t('scan_passport.scan_page_title')}
              </h2>

              <div className={s.infoBlock}>
                <div className={s.infoText}>
                  <h3 className={clsx(s.infoTitle, 'semi-bold-17')}>
                    {t('scan_passport.scan_title')}
                  </h3>
                  <p className={clsx(s.infoDescription, 'regular-13')}>
                    {t('scan_passport.scan_description')}
                  </p>
                </div>

                <ImageLazy
                  alt="passportId"
                  src={PassExampleId}
                  className={s.image}
                />
              </div>

              <div className={s.infoBlock}>
                <div className={s.infoText}>
                  <h3 className={clsx(s.infoTitle, 'semi-bold-17')}>
                    {t('scan_passport.scan_travel_pass')}
                  </h3>
                  <p className={clsx(s.infoDescription, 'regular-13')}>
                    {t('scan_passport.scan_description')}
                  </p>
                </div>

                <ImageLazy
                  alt="passport"
                  src={PassExamplePassport}
                  className={s.image}
                />
              </div>
            </div>

            <ButtonContinue onClick={() => setShowCamera(true)}>
              <span className={clsx(s.buttonText, 'semi-bold-17')}>
                {t('scan_passport.scan')}
              </span>
            </ButtonContinue>
          </div>
        )}
      </div>
    </>
  )
}

export default ScanPassportInsurancePage
